import { IVec2 } from "@base/math/Vec2";
import { IVec3, Vec3 } from "@base/math/Vec3";

export class ConcavityHelper {
  public from: IVec3;
  public vec: IVec3;
  public normal: IVec3;
  public len: number;
  public steps: number;
  public mul: number;
  public concavity: number;

  public constructor(from: IVec3, to: IVec3, concavity: number, mul: number = 1, steps?: number) {
    this.concavity = concavity;
    this.mul = mul;
    this.from = from;
    this.vec = Vec3.sub(to, from);
    this.len = Vec3.len(this.vec);
    this.steps = steps !== undefined ? steps : (this.concavity === 0 ? 2 : 3 + Math.round(this.len * 2));
    const vecNormalized = Vec3.mul(this.vec, 1 / this.len);
    this.normal = { x: -vecNormalized.z, y: vecNormalized.y, z: vecNormalized.x };
  }


  static calcFactor(step: number, steps: number): number {
    if (steps <= 1) return 0;
    return step / (steps - 1);
  }

  public calcStep(step: number): IVec3 {
    const factor = ConcavityHelper.calcFactor(step, this.steps);
    return this.calcPointByFactor(factor);
  }

  public calcPointByFactor(factor: number): IVec3 {
    const w = -1 + 2 * factor;
    const v = (1 - w * w) * this.len * (this.concavity / 100) * this.mul;

    return {
      x: this.from.x + this.vec.x * factor + this.normal.x * v,
      y: this.from.y + this.vec.y * factor + this.normal.y * v,
      z: this.from.z + this.vec.z * factor + this.normal.z * v
    }
  }

  public calcNormalByFactor(factor: number): IVec3 {
    const w = -1 + 2 * factor;
    const v = (1 - w * w) * this.len * (this.concavity / 100) * this.mul;

    return {
      x: this.normal.x * v,
      y: this.normal.y * v,
      z: this.normal.z * v
    }
  }

  public calcStepArray(options?: { steps?: number, stepOffset?: number }): IVec3[] {
    const stepOffset = options?.stepOffset || 0;
    const steps = options?.steps === undefined ? this.steps : options.steps;
    const ret: IVec3[] = [];
    for (let i = 0; i < steps; i++) {
      ret.push(this.calcStep(i + stepOffset))
    }
    return ret;
  }

  static calcPointString(points: IVec2[]): string {
    let pointString = "";
    for (const p of points) {
      if (isNaN(p.x)) return "";
      pointString += p.x + " " + p.y + " ";
    }
    return pointString;
  }

  static calcConcavityDist(len: number, concavity: number, factor: number): number {
    const factor2 = -1 + factor * 2;
    return (1 - factor2 * factor2) * len * (concavity / 100);
  }
}
